import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '../lib/cn.ts';
import { springs } from '../motion/springs.ts';

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: 'default' | 'success' | 'error';
  action?: { label: ReactNode; onClick: () => void };
  durationMs?: number;
}
interface ToastItem extends ToastOptions {
  id: number;
}
interface ToastApi {
  toast: (o: ToastOptions) => void;
  dismiss: (id: number) => void;
}
const Ctx = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be inside <ToastProvider>');
  return v;
}

/** Bottom toasts (above the tab bar). glass-3. */
export function ToastProvider({ children, bottomOffset = 96 }: { children: ReactNode; bottomOffset?: number }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const toast = useCallback(
    (o: ToastOptions) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-2), { ...o, id }]);
      setTimeout(() => dismiss(id), o.durationMs ?? 4000);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 z-[var(--m-z-toast)] mx-auto flex w-full max-w-[var(--m-size-layout-max)] flex-col items-center gap-2 px-4" style={{ bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))` }}>
        <AnimatePresence initial={false}>
          {items.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={springs.default}
              role="status"
              className={cn('glass-3 pointer-events-auto flex w-full items-center gap-3 rounded-xl px-4 py-3', t.tone === 'error' && 'border-status-cancelled/40', t.tone === 'success' && 'border-status-ontime/40')}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-body font-semibold">{t.title}</div>
                {t.description && <div className="text-caption text-text-secondary">{t.description}</div>}
              </div>
              {t.action && (
                <button
                  type="button"
                  className="shrink-0 rounded-md px-2 py-1 text-caption font-semibold text-tint"
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </Ctx.Provider>
  );
}
